import React, { useCallback, useEffect, useRef, useState } from 'react';
import NewWindow from 'react-new-window';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, Typography } from '@mui/material';

import { faChevronUp, faNoteSticky } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { RootState } from '../../../app/rootReducer';
import { AppDispatch } from '../../../app/store';
import { Resource } from '../../../common/constants/resource';
import { getAllTasks } from '../../tasks/slice/taskListSlice';
import { TaskTypes } from '../../tasks/types/taskTypes';
import {
  closeDrawer,
  requestOpen,
  toggleNotesDrawer,
} from '../drawer/drawerCoordSlice';
import { INote } from '../interfaces/INote';
import { DraftColumn } from './DraftColumn';
import { HistoryColumn } from './HistoryColumn';
import { loadDraft, notesDraftActions } from './notesDraftSlice';
import styles from './notesDrawer.module.scss';
import { postNote } from './notesSlice';
import { useAutoSaveDraft } from './useAutoSaveDraft';
import { useNotesBottomDrawerLayout } from './useNotesBottomDrawerLayout';

// Returns the MIB codes of the last submitted note, but only if that note was
// created after the most recent MIB_CODEBACK task. Used to pre-populate a fresh draft.
const pickNoteMibCodesAfterLastCodebackTask = (state: RootState): string[] => {
  const notes = state.notes.notes;
  if (!notes || notes.length === 0) return [];

  const lastNote = notes[notes.length - 1];
  if (!lastNote.mibCodes || lastNote.mibCodes.length === 0) return [];

  const tasks = getAllTasks(state.taskList);
  let latestCodebackTime = 0;
  for (const task of tasks) {
    if (task.taskType === TaskTypes.MIB_CODEBACK && task.createdAt) {
      const t = new Date(task.createdAt).getTime();
      if (t > latestCodebackTime) latestCodebackTime = t;
    }
  }

  const lastNoteTime = new Date(lastNote.date).getTime();
  if (latestCodebackTime > 0 && lastNoteTime <= latestCodebackTime) return [];

  return lastNote.mibCodes;
};

interface NotesBottomDrawerProps {
  applicationId: string;
}

export const NotesBottomDrawer: React.FC<NotesBottomDrawerProps> = ({
  applicationId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { visible: isDrawerVisible } = useNotesBottomDrawerLayout();

  const isOpen = useSelector((state: RootState) => state.notesDraft.isOpen);
  const isLoading = useSelector(
    (state: RootState) => state.notesDraft.isLoading,
  );
  const loadedApplicationId = useSelector(
    (state: RootState) => state.notesDraft.loadedApplicationId,
  );
  const lastSavedSnapshot = useSelector(
    (state: RootState) => state.notesDraft.lastSavedSnapshot,
  );
  const lastSavedMibCodes = useSelector(
    (state: RootState) => state.notesDraft.lastSavedMibCodes,
  );
  const lastSavedAt = useSelector(
    (state: RootState) => state.notesDraft.lastSavedAt,
  );
  const noteMibCodesAfterLastCodebackTask = useSelector(
    pickNoteMibCodesAfterLastCodebackTask,
  );
  const submittedCount = useSelector(
    (state: RootState) => state.notes.notes.length,
  );

  const activeDrawer = useSelector(
    (state: RootState) => state.drawerCoord.activeDrawer,
  );

  const [content, setContent] = useState<string>('');
  const [mibCodes, setMibCodes] = useState<string[]>([]);
  const [showPopout, setShowPopout] = useState(false);
  const popOutRef = useRef<Window | null>(null);
  const hydratedFromLoadRef = useRef(false);

  useEffect(() => {
    hydratedFromLoadRef.current = false;
    setContent('');
    setMibCodes([]);
    dispatch(notesDraftActions.resetDraft());
    dispatch(loadDraft(applicationId));
  }, [applicationId, dispatch]);

  useEffect(() => {
    if (
      !isLoading &&
      !hydratedFromLoadRef.current &&
      loadedApplicationId === applicationId
    ) {
      setContent(current => (current.length > 0 ? current : lastSavedSnapshot));
      setMibCodes(current => {
        if (current.length > 0) return current;
        if (lastSavedMibCodes.length > 0) return lastSavedMibCodes;
        // No saved draft (lastSavedAt is null): pre-populate from recent notes.
        // If a draft was saved but with empty codes (user deliberately cleared them),
        // lastSavedAt will be non-null and we respect the empty state.
        if (lastSavedAt !== null) return [];
        return noteMibCodesAfterLastCodebackTask;
      });
      hydratedFromLoadRef.current = true;
    }
  }, [
    isLoading,
    loadedApplicationId,
    applicationId,
    lastSavedSnapshot,
    lastSavedMibCodes,
    lastSavedAt,
    noteMibCodesAfterLastCodebackTask,
  ]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.shiftKey && event.code === 'Space') {
        const target = event.target as HTMLElement | null;
        // Don't hijack the spacebar while typing in the draft (or any other input).
        if (
          target &&
          (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT')
        ) {
          return;
        }
        if (!isDrawerVisible) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        dispatch(toggleNotesDrawer());
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dispatch, isDrawerVisible]);

  const autoSaveEnabled = isOpen || showPopout;
  const { flushNow } = useAutoSaveDraft({
    applicationId,
    content,
    mibCodes,
    enabled: autoSaveEnabled,
  });

  useEffect(() => {
    if (!hydratedFromLoadRef.current) return;
    if (lastSavedAt === null) {
      setMibCodes(noteMibCodesAfterLastCodebackTask);
    }
  }, [noteMibCodesAfterLastCodebackTask, lastSavedAt]);

  const prevActiveDrawerRef = useRef(activeDrawer);
  useEffect(() => {
    if (
      activeDrawer?.type === 'eti' &&
      prevActiveDrawerRef.current?.type !== 'eti'
    ) {
      flushNow();
    }
    prevActiveDrawerRef.current = activeDrawer;
  }, [activeDrawer, flushNow]);

  const handleRetrieve = useCallback(
    (note: INote) => {
      if (content.trim().length > 0) {
        const ok = window.confirm(
          'Retrieving will replace your current Notes with the saved version. Continue?',
        );
        if (!ok) {
          return;
        }
      }
      setContent(note.note);
      setMibCodes(note.mibCodes ?? []);
    },
    [content],
  );

  const handlePopOutToggle = async () => {
    await flushNow();
    if (showPopout) {
      setShowPopout(false);
      dispatch(requestOpen({ type: 'notes' }));
    } else {
      dispatch(notesDraftActions.closeDrawer());
      setShowPopout(true);
    }
  };

  const handleSubmit = async () => {
    await flushNow();
    const success = await dispatch(postNote(applicationId, content, mibCodes));
    if (!success) {
      return;
    }
    // After submitting, the note we just wrote becomes the last note.
    // Pre-populate the next fresh draft with its codes (if any).
    setContent('');
    setMibCodes(mibCodes);
    dispatch(notesDraftActions.resetDraft());
    dispatch(closeDrawer({ type: 'notes' }));
    hydratedFromLoadRef.current = true;
  };

  if (!isDrawerVisible) {
    return null;
  }

  const tabIconRotation = isOpen ? styles.ChevronUp : '';

  return (
    <>
      <Box className={styles.DrawerRoot}>
        <Box className={`${styles.Panel} ${isOpen ? styles.PanelOpen : ''}`}>
          <Box className={styles.DrawerInner}>
            <DraftColumn
              applicationId={applicationId}
              content={content}
              onChange={setContent}
              mibCodes={mibCodes}
              onMibCodesChange={setMibCodes}
              onSubmit={handleSubmit}
              onPopOutToggle={handlePopOutToggle}
              isPoppedOut={showPopout}
            />
            <HistoryColumn onRetrieve={handleRetrieve} />
          </Box>
        </Box>
        <Box
          className={styles.Tab}
          onClick={() => dispatch(toggleNotesDrawer())}
          role="button"
          tabIndex={0}
          title="Toggle notes (Shift+Space)"
          display="flex"
          alignItems="center"
          gap={1.25}
          px={2}
        >
          <Box
            color="text.secondary"
            display="flex"
            alignItems="center"
            fontSize={15}
          >
            <FontAwesomeIcon icon={faNoteSticky} />
          </Box>
          <Typography variant="body2" fontWeight={500}>
            {Resource.TASK_TITLE_NOTES}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {submittedCount} submitted
          </Typography>
          {content.trim().length > 0 && (
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              title="Unsaved draft"
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#f59e0b',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: 11,
                  color: '#1a1a18',
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                Unsubmitted draft
              </Typography>
            </Box>
          )}
          <Box ml="auto" display="flex" alignItems="center" gap={1}>
            <Chip
              label="⇧ Space"
              size="small"
              sx={{
                fontSize: 9,
                height: 16,
                fontFamily: 'ui-monospace, Menlo, monospace',
                color: 'text.disabled',
                bgcolor: '#f5f5f3',
                border: '0.5px solid rgba(0,0,0,0.22)',
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
            <FontAwesomeIcon
              icon={faChevronUp}
              className={`${styles.Chevron} ${tabIconRotation}`}
              fontSize={12}
            />
          </Box>
        </Box>
      </Box>

      {showPopout && (
        <NewWindow
          title={`Notes · Application ${applicationId}`}
          features={{
            height: Math.floor(window.innerHeight * 0.7),
            width: Math.floor(window.innerWidth / 2),
          }}
          center="parent"
          onUnload={() => {
            if (popOutRef.current && popOutRef.current.closed) {
              setShowPopout(false);
              dispatch(requestOpen({ type: 'notes' }));
            }
          }}
          onBlock={() => {
            // eslint-disable-next-line no-alert
            alert('Pop-out blocked. Please allow popups for this site.');
            setShowPopout(false);
            dispatch(requestOpen({ type: 'notes' }));
          }}
        >
          <Box height="100vh" display="flex" flexDirection="column">
            <Box className={styles.DrawerInner} flex={1}>
              <DraftColumn
                applicationId={applicationId}
                content={content}
                onChange={setContent}
                mibCodes={mibCodes}
                onMibCodesChange={setMibCodes}
                onSubmit={handleSubmit}
                onPopOutToggle={handlePopOutToggle}
                isPoppedOut
              />
              <HistoryColumn onRetrieve={handleRetrieve} />
            </Box>
          </Box>
        </NewWindow>
      )}
    </>
  );
};
